import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { Box, Link } from "@mui/joy";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import {
  ApiCompetition,
  ApiLeagueEntryInline,
  ApiLeagueEntryInlineRoundSummary,
} from "griptonite-api-client";
import React, { useCallback, useMemo, useRef } from "react";

import { useLeagueQuery } from "@/hooks/competitions";
import formatNumber, { getNumberWithOrdinal } from "@/utils/formatNumber";

import Avatar from "../Avatar";
import { useModal } from "../modals/useModal";
import { useLeagueEntriesTableContext } from "./LeagueEntriesContext";
import {
  CompetititorCellStyled,
  EmptyCell,
  RoundScoreCellStyled,
  StyledTable,
} from "./LeagueEntriesTable.style";

type Props = { leagueUuid: string; entries: ApiLeagueEntryInline[] };

const emptyArray: never[] = [];

const CompetitorCell = React.memo(({ entry }: { entry: ApiLeagueEntryInline }) => {
  const { league, categoriesByUuid, mergeSourceUserUuid, setMergeSourceUserUuid } =
    useLeagueEntriesTableContext();
  const { display: displayModal } = useModal();
  const category = categoriesByUuid[entry.category];
  return (
    <CompetititorCellStyled
      from="/gym/$venueUuid/events/series/$leagueUuid/competitors"
      to="./$userUuid"
      params={{
        userUuid: entry.user.uuid,
      }}
      replace
      onClick={(e) => {
        if (mergeSourceUserUuid && league) {
          // If we are merging, open the merge modal instead
          e.stopPropagation();
          e.preventDefault();
          displayModal("MERGE_LEAGUE_ENTRIES", {
            sourceUuid: mergeSourceUserUuid,
            targetUuid: entry.user.uuid,
            leagueUuid: league.uuid,
            clearMergeState: () => setMergeSourceUserUuid(undefined),
          });
        }
      }}
    >
      <Avatar user={entry.user} size={32} />
      <div className="right">
        <div className="user">
          {entry.user.firstName} {entry.user.lastName}
        </div>
        <div className="category">
          <strong>{entry.position ? getNumberWithOrdinal(entry.position) : null}</strong>,{" "}
          {category?.name}
        </div>
      </div>
    </CompetititorCellStyled>
  );
});
CompetitorCell.displayName = "CompetitorCell";

const CompetitionHeader = React.memo(({ competition }: { competition: ApiCompetition }) => {
  return competition.name;
});
CompetitionHeader.displayName = "CompetitionHeader";

const RoundScoreCell = React.memo(({ round }: { round: ApiLeagueEntryInlineRoundSummary }) => {
  return (
    <RoundScoreCellStyled>
      <div className="score">{formatNumber(round.score, { maxDecimals: 1 })}pts</div>
      {round.position ? (
        <div className="position">{getNumberWithOrdinal(round.position)}</div>
      ) : null}
    </RoundScoreCellStyled>
  );
});
RoundScoreCell.displayName = "RoundScoreCell";

const TotalCell = React.memo(({ entry }: { entry: ApiLeagueEntryInline }) => {
  return <>{formatNumber(entry.score, { maxDecimals: 1 })}pts</>;
});
TotalCell.displayName = "TotalCell";

const LeagueEntryRow = React.memo(
  ({
    row,
    virtualRow,
    mergeSourceUserUuid,
  }: {
    row: Row<ApiLeagueEntryInline>;
    virtualRow: VirtualItem;
    mergeSourceUserUuid?: string;
  }) => {
    return (
      <tr
        key={row.id}
        style={{
          height: `${virtualRow.size}px`,
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          transform: `translateY(${virtualRow.start}px)`,

          // If this is the source user for a merge, appear disabled
          ...(mergeSourceUserUuid === row.original.user.uuid
            ? {
                opacity: 0.5,
                pointerEvents: "none",
              }
            : {}),
        }}
      >
        {row.getVisibleCells().map((cell) => {
          return cell.column.id === "competitor" ? (
            <th key={cell.id} className={cell.column.columnDef.meta?.className}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </th>
          ) : (
            <td key={cell.id} className={cell.column.columnDef.meta?.className}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          );
        })}
      </tr>
    );
  },
);
LeagueEntryRow.displayName = "LeagueEntryRow";

export default function LeagueEntriesTable({ leagueUuid, entries }: Props) {
  const { data: league } = useLeagueQuery(leagueUuid);
  const { selectedCategoryUuid, competitorSearchQuery, mergeSourceUserUuid } =
    useLeagueEntriesTableContext();

  const competitions = useMemo(() => {
    return league?.competitions || emptyArray;
  }, [league]);

  const rowHeight = 50;

  const wrapperRef = useRef<HTMLDivElement>(null);

  const isLongTotal = useMemo(() => {
    return entries.some((entry) => entry.score > 1e6);
  }, [entries]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      const categoryMatches = !selectedCategoryUuid || entry.category === selectedCategoryUuid;
      if (!categoryMatches) {
        return false;
      }
      const searchMatches =
        !competitorSearchQuery ||
        entry.user.name.toLowerCase().includes(competitorSearchQuery.toLowerCase());
      return searchMatches;
    });
  }, [entries, selectedCategoryUuid, competitorSearchQuery]);

  // const { userUuid: selectedUserUuid } = useParams({ strict: false });

  // const entriesRef = useRef(entries);
  // useEffect(() => {
  //   entriesRef.current = entries;
  // }, [entries]);

  // useEffect(() => {
  //   if (selectedUserUuid) {
  //     const index = entriesRef.current.findIndex(
  //       (entry) => entry.user.uuid === selectedUserUuid
  //     );
  //     if (index !== -1) {
  //       rowVirtualizer.scrollToIndex(index);
  //     }
  //   }
  // }, [selectedUserUuid, rowVirtualizer]);

  const columns = useMemo(() => {
    return [
      {
        id: "competitor",
        accessorKey: "user.name",
        cell: ({ row }) => <CompetitorCell entry={row.original} />,
        meta: {
          className: "competitor",
        },
        header: "Competitor",
      },
      {
        id: "position",
        accessorKey: "position",
        header: "Position",
        cell: ({ row }) => {
          const position = row.original.position;
          return position ? getNumberWithOrdinal(position) : null;
        },
        meta: {
          className: "position",
        },
        sortDescFirst: false,
      },
      {
        id: "total",
        accessorKey: "score",
        cell: ({ row }) => <TotalCell entry={row.original} />,
        header: () => "Total Score",
        meta: {
          className: "total",
        },
      },
      ...(competitions.map((competition) => ({
        id: `competition_${competition.uuid}`,
        accessorFn: (row) =>
          row.rounds.find((round) => round.competition === competition.uuid)?.position,
        cell: ({ row }) => {
          const round = row.original.rounds.find((round) => round.competition === competition.uuid);
          return round ? <RoundScoreCell round={round} /> : <EmptyCell />;
        },
        header: () => <CompetitionHeader competition={competition} />,
        meta: {
          className: "round",
        },
        sortDescFirst: false,
        sortingFn: (rowA, rowB, columnId) => {
          const competitionUuid = columnId.split("_")[1];
          const roundA = rowA.original.rounds.find(
            (round) => round.competition === competitionUuid,
          );
          const roundB = rowB.original.rounds.find(
            (round) => round.competition === competitionUuid,
          );
          if (!roundA && !roundB) {
            return 0;
          }
          if (!roundA) {
            return 1;
          }
          if (!roundB) {
            return -1;
          }
          return (roundA.position || Infinity) - (roundB.position || Infinity);
        },
      })) as ColumnDef<ApiLeagueEntryInline, unknown>[]),
    ] as ColumnDef<
      ApiLeagueEntryInline,
      {
        meta: {
          className: string;
        };
      }
    >[];
  }, [competitions]);

  const initialState = useMemo(() => {
    return {
      sorting: [{ id: "position", desc: false }],
    };
  }, []);

  const table = useReactTable({
    data: filteredEntries,
    getRowId: (row) => row.user.uuid,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState,
    columns,
    enableSortingRemoval: false,
  });

  const rows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight,
    getItemKey: useCallback((index: number) => rows[index].id, [rows]),
    overscan: 8,
    getScrollElement: () => wrapperRef.current,
  });

  if (!league) {
    return null;
  }

  return (
    <Box ref={wrapperRef} sx={{ position: "relative", overflow: "auto", flex: 1, width: "100%" }}>
      <StyledTable rowHeight={rowHeight} isLongTotal={isLongTotal}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const isSorted = header.column.getIsSorted();
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={header.column.columnDef.meta?.className}
                  >
                    {header.isPlaceholder ? null : (
                      <Link
                        onClick={header.column.getToggleSortingHandler()}
                        startDecorator={
                          isSorted ? (
                            isSorted === "desc" ? (
                              <ArrowDownward fontSize="small" />
                            ) : (
                              <ArrowUpward fontSize="small" />
                            )
                          ) : null
                        }
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Link>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody
          style={{
            position: "relative",
            height: `${rowVirtualizer.getTotalSize()}px`,
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];

            return (
              <LeagueEntryRow
                key={row.id}
                row={row}
                virtualRow={virtualRow}
                mergeSourceUserUuid={mergeSourceUserUuid}
              />
            );
          })}
        </tbody>
      </StyledTable>
    </Box>
  );
}
